import { useState, useEffect, useMemo } from 'react';
import { calculateArbitrageOpportunities } from '../business-logic/arbitrageCalculator.js';
import { calculateTransportCost, calculateImportTax } from '../business-logic/costCalculator.js';
import { normalizeFuel } from '../utils/normalization.js';

export function useOpportunities(allListings, filters, favourites, showFavourites) {
  const {
    sourceCountry,
    targetCountry,
    minProfit,
    filterMake,
    filterModel,
    filterVersion,
    filterFuelType,
    minMileage,
    maxMileage,
    minYear,
    maxYear,
    maxPrice,
    sortBy,
    sortDir,
    qualityLevels,
    yearTolerance,
    mileageTolerance,
  } = filters;

  const [opportunities, setOpportunities] = useState([]);
  const [computingOpps, setComputingOpps] = useState(true);

  // Recalculate opportunities when source/target country or listings change
  useEffect(() => {
    if (allListings.length === 0) return;

    setComputingOpps(true);
    const opps = calculateArbitrageOpportunities(
      allListings,
      sourceCountry,
      targetCountry,
      calculateTransportCost,
      calculateImportTax,
      { yearTolerance, mileageTolerance }
    );
    setOpportunities(opps);
    setComputingOpps(false);
  }, [allListings, sourceCountry, targetCountry, yearTolerance, mileageTolerance]);

  // Apply frontend filters
  const filteredOpps = useMemo(() => {
    return opportunities.filter(opp => {
      const profitMatch = opp.netProfit >= minProfit;
      const makeMatch = filterMake === 'all' || (opp.make || '').trim().toLowerCase() === filterMake.trim().toLowerCase();
      const modelMatch = filterModel === 'all' || opp.model === filterModel;
      const versionMatch = filterVersion === 'all' || opp.normalizedVersion === filterVersion;
      const fuelTypeMatch = filterFuelType === 'all' || normalizeFuel(opp.fuelType) === filterFuelType;
      const mileageMatch = opp.mileage >= minMileage && opp.mileage <= maxMileage;
      const yearMatch = opp.year >= minYear && opp.year <= maxYear;
      const priceMatch = opp.sourcePrice <= maxPrice;
      const qualityMatch = qualityLevels.length === 0 || qualityLevels.some(level => {
        if (level === 'high') return opp.qualityScore != null && opp.qualityScore >= 8;
        if (level === 'medium') return opp.qualityScore != null && opp.qualityScore >= 5 && opp.qualityScore <= 7;
        if (level === 'low') return opp.qualityScore != null && opp.qualityScore <= 4;
        return false;
      });
      return profitMatch && makeMatch && modelMatch && versionMatch && fuelTypeMatch && mileageMatch && yearMatch && priceMatch && qualityMatch;
    });
  }, [opportunities, minProfit, filterMake, filterModel, filterVersion, filterFuelType, minMileage, maxMileage, minYear, maxYear, maxPrice, qualityLevels]);

  // Sorted and filtered by favourites
  const displayedOpps = useMemo(() => {
    return (showFavourites
      ? filteredOpps.filter(opp => favourites.includes(opp.id))
      : filteredOpps
    ).slice().sort((a, b) => {
      const valA = sortBy === 'profit' ? a.netProfit : a.sourcePrice;
      const valB = sortBy === 'profit' ? b.netProfit : b.sourcePrice;
      return sortDir === 'desc' ? valB - valA : valA - valB;
    });
  }, [filteredOpps, showFavourites, favourites, sortBy, sortDir]);

  const totalProfitPotential = useMemo(() => {
    return filteredOpps.reduce((sum, opp) => sum + opp.netProfit, 0);
  }, [filteredOpps]);

  const filteredListingsCount = useMemo(() => {
    return allListings.filter(car => {
      const makeMatch = filterMake === 'all' || (car.make || '').trim().toLowerCase() === filterMake.trim().toLowerCase();
      const modelMatch = filterModel === 'all' || car.model === filterModel;
      const versionMatch = filterVersion === 'all' || car.normalized_version === filterVersion;
      const fuelTypeMatch = filterFuelType === 'all' || normalizeFuel(car.fuel_type) === filterFuelType;
      const mileageMatch = (car.mileage || 0) >= minMileage && (car.mileage || 0) <= maxMileage;
      const yearMatch = (car.year || 0) >= minYear && (car.year || 0) <= maxYear;
      const priceMatch = (car.price || 0) <= maxPrice;
      return makeMatch && modelMatch && versionMatch && fuelTypeMatch && mileageMatch && yearMatch && priceMatch;
    }).length;
  }, [allListings, filterMake, filterModel, filterVersion, filterFuelType, minMileage, maxMileage, minYear, maxYear, maxPrice]);

  return {
    opportunities,
    filteredOpps,
    displayedOpps,
    totalProfitPotential,
    filteredListingsCount,
    computingOpps,
  };
}
